/**
 * Semantic colour roles per colour scheme (DS-01). Both schemes have exactly the same keys; the key
 * is the CSS custom property name (`--surface`, `--text-muted`, …) and the MUI palette reads the
 * same values. Components use these roles, never the palette.
 *
 * Fills keep the exact brand and status values in both schemes (gold #F8B818, navy #102050, the
 * status bases). Only colours used as text, links or focus rings change in the dark scheme, to the
 * tints approved with DS-01.
 */
import { colors, palette, statusTints } from './foundation.js';
const status = (scheme) => ({
    success: palette.success,
    'success-on': palette.neutral[0],
    'success-text': statusTints.success[`${scheme}Text`],
    'success-subtle': statusTints.success[`${scheme}Subtle`],
    // White on the warning and info fills fails AA (3.11 and 3.86): their text is dark.
    warning: palette.warning,
    'warning-on': palette.neutral[900],
    'warning-text': statusTints.warning[`${scheme}Text`],
    'warning-subtle': statusTints.warning[`${scheme}Subtle`],
    danger: palette.error,
    'danger-on': palette.neutral[0],
    'danger-text': statusTints.error[`${scheme}Text`],
    'danger-subtle': statusTints.error[`${scheme}Subtle`],
    info: palette.info,
    'info-on': palette.neutral[900],
    'info-text': statusTints.info[`${scheme}Text`],
    'info-subtle': statusTints.info[`${scheme}Subtle`],
});
/** The legacy navy top bar is navy in both schemes, so it has its own roles. */
const topbar = {
    'topbar-bg': colors.secondary,
    'topbar-text': palette.neutral[0],
    'topbar-text-muted': palette.navy[100],
    'topbar-hover': palette.navy[600],
    'topbar-focus-ring': palette.gold[500],
};
export const semanticLight = {
    bg: palette.neutral[100],
    surface: palette.neutral[0],
    'surface-raised': palette.neutral[0],
    'surface-sunken': palette.neutral[50],
    overlay: 'rgba(6, 16, 43, 0.5)',
    text: palette.neutral[900],
    'text-muted': palette.neutral[600],
    'text-subtle': palette.neutral[550],
    'text-disabled': palette.neutral[400],
    'text-on-primary': colors.primaryContrast,
    'text-on-secondary': colors.secondaryContrast,
    border: palette.neutral[200],
    'border-strong': palette.neutral[500],
    primary: colors.primary,
    'primary-hover': palette.gold[600],
    'primary-active': palette.gold[700],
    'primary-subtle': palette.gold[50],
    // Gold text on white is 1.77:1, so primary-coloured text (ghost and outline buttons) is navy.
    'primary-text': palette.navy[700],
    secondary: colors.secondary,
    'secondary-hover': palette.navy[600],
    'secondary-text': palette.navy[700],
    link: palette.navy[700],
    // Gold on white is 1.77:1, so the light focus ring is navy.
    'focus-ring': palette.navy[700],
    // Checked checkbox, radio and switch: gold fails the 3:1 state contrast on white.
    'control-checked': palette.navy[700],
    hover: 'rgba(16, 32, 80, 0.06)',
    selected: 'rgba(248, 184, 24, 0.16)',
    ...status('light'),
    'table-header': colors.tableHeader,
    'table-header-text': palette.neutral[900],
    'filter-pill': colors.filterPill,
    'filter-pill-text': colors.filterPillContrast,
    ...topbar,
    'shadow-sm': '0 1px 2px rgba(16, 32, 80, 0.08)',
    'shadow-md': '0 4px 12px rgba(16, 32, 80, 0.10)',
    'shadow-lg': '0 12px 40px rgba(16, 32, 80, 0.12)',
    'shadow-xl': '0 24px 64px rgba(16, 32, 80, 0.16)',
};
export const semanticDark = {
    bg: palette.neutral[900],
    surface: palette.neutral[850],
    'surface-raised': palette.neutral[800],
    'surface-sunken': palette.neutral[950],
    overlay: 'rgba(0, 0, 0, 0.64)',
    text: palette.neutral[50],
    'text-muted': palette.neutral[300],
    'text-subtle': palette.neutral[400],
    'text-disabled': palette.neutral[500],
    'text-on-primary': colors.primaryContrast,
    'text-on-secondary': colors.secondaryContrast,
    border: palette.neutral[700],
    'border-strong': palette.neutral[500],
    primary: colors.primary,
    'primary-hover': palette.gold[400],
    'primary-active': palette.gold[300],
    'primary-subtle': palette.gold[950],
    'primary-text': palette.gold[500],
    secondary: colors.secondary,
    'secondary-hover': palette.navy[600],
    // Navy text on dark surfaces is 1.18:1; the approved dark tint is navy-200.
    'secondary-text': palette.navy[200],
    link: palette.navy[200],
    'focus-ring': palette.navy[200],
    // Navy fails on dark surfaces (1.18:1); the approved navy-200 tint passes.
    'control-checked': palette.navy[200],
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(248, 184, 24, 0.16)',
    ...status('dark'),
    'table-header': palette.navy[800],
    'table-header-text': palette.neutral[50],
    'filter-pill': colors.filterPill,
    'filter-pill-text': colors.filterPillContrast,
    ...topbar,
    // Softer in the dark scheme: shadows barely read on dark surfaces, so a faint top highlight
    // carries the elevation instead.
    'shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.24)',
    'shadow-md': '0 4px 12px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
    'shadow-lg': '0 12px 32px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    'shadow-xl': '0 24px 48px rgba(0, 0, 0, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
};
export const colorSchemes = { light: semanticLight, dark: semanticDark };
//# sourceMappingURL=semantic.js.map